/**
 * The checks an incoming invoice is reviewed on, grouped as the review tab lists them. The list
 * is the single source of truth: the server implements one function per key and records one
 * `incoming_invoice_checks` row per key, and the client renders the groups in this order. A new
 * check is one entry here, its implementation on the server, and its locale key.
 */
export const INCOMING_INVOICE_CHECK_GROUPS = ["formal", "arithmetic"] as const;

export type IncomingInvoiceCheckGroup = (typeof INCOMING_INVOICE_CHECK_GROUPS)[number];

export const INCOMING_INVOICE_CHECKS = [
	{ key: "number-present", group: "formal" },
	{ key: "issuer-assigned", group: "formal" },
	{ key: "invoice-date-present", group: "formal" },
	{ key: "tax-id-known", group: "formal" },
	{ key: "iban-known", group: "formal" },
	{ key: "no-duplicate", group: "formal" },
	{ key: "gross-matches-net-plus-vat", group: "arithmetic" },
	{ key: "positions-match-net", group: "arithmetic" },
	{ key: "cost-allocation-complete", group: "arithmetic" },
] as const satisfies ReadonlyArray<{ key: string; group: IncomingInvoiceCheckGroup }>;

export type IncomingInvoiceCheckKey = (typeof INCOMING_INVOICE_CHECKS)[number]["key"];

/**
 * What a check found beyond its state, for a reader who wants to know why: the figures an
 * arithmetic check compared, or the fields a check found missing.
 */
export interface IncomingInvoiceCheckDetails {
	expected?: number;
	actual?: number;
	difference?: number;
	missing?: string[];
	reason?: string;
	/** The earlier invoice a duplicate repeats, and its number. */
	duplicateOfId?: string;
	duplicateOfNumber?: string | null;
	/** The IBAN an invoice asks to be paid on, while its contact does not hold it. */
	iban?: string;
}
